/**
 * driveStore — central reactive state for the Drive file manager UI.
 *
 * Built on @preact/signals. All user interactions mutate this store;
 * Preact components subscribe automatically via signal reads.
 *
 * CSR mode: all data is fetched from JSON API endpoints; no Turbo Streams
 * are used for content delivery. DOM updates are driven by signals.
 *
 * Listing uses infinite scroll (default page size 100). Sort is server-side
 * across pages. Preview poll hits a lightweight statuses endpoint.
 */

import { signal, computed, batch } from '@preact/signals'

const PREVIEW_POLL_INTERVAL_MS = 2500
const PREVIEW_POLL_MAX_ATTEMPTS = 24 // ~60s
const DEFAULT_PAGE_LIMIT = 100
const PREVIEW_TERMINAL_STATES = new Set([
  'success', 'failed', 'error', 'protected', 'not_previewable'
])

function isPreviewableContentType(contentType) {
  if (!contentType) return false
  return contentType.startsWith('image/')
    || contentType.startsWith('video/')
    || contentType.startsWith('audio/')
    || contentType === 'application/pdf'
    || contentType.includes('officedocument')
    || contentType === 'application/msword'
    || contentType === 'application/vnd.ms-excel'
}

function fileNeedsPreviewPoll(file) {
  if (file.preview_url) return false
  if (!isPreviewableContentType(file.content_type)) return false
  const state = (file.preview_state || '').toString()
  if (PREVIEW_TERMINAL_STATES.has(state)) return false
  return true
}

function withListParams(path, { offset = 0, limit = DEFAULT_PAGE_LIMIT, sortColumn = null, sortDir = 'asc' } = {}) {
  const url = new URL(path, window.location.origin)
  url.searchParams.set('limit', String(limit))
  url.searchParams.set('offset', String(offset))
  if (sortColumn) {
    url.searchParams.set('sort', sortColumn)
    url.searchParams.set('dir', sortDir)
  }
  return url.pathname + url.search
}

// ── Store factory ──────────────────────────────────────────────────────────────

export function createDriveStore() {

  // ── Data ──────────────────────────────────────────────────────────────────

  const folders       = signal([])
  const files         = signal([])
  const breadcrumbs   = signal([])
  const currentFolder = signal(null)

  // ── Selection ─────────────────────────────────────────────────────────────

  // IDs are namespaced: 'folder-{id}' or 'file-{id}'
  const selectedIds   = signal([])

  // ── Status ────────────────────────────────────────────────────────────────

  const loading       = signal(false)
  const loadingMore   = signal(false)
  const error         = signal(null)
  const hasMore       = signal(false)
  const nextOffset    = signal(0)

  // ── Search ────────────────────────────────────────────────────────────────

  const searchQuery   = signal('')

  // ── Sort (server-side across pages) ───────────────────────────────────────

  // null = server default order until user clicks a column header
  const sortColumn    = signal(null) // 'name' | 'size' | 'created'
  const sortDir       = signal('asc') // 'asc' | 'desc'

  // ── Preview poll / list path ──────────────────────────────────────────────

  let previewPollTimer = null
  let previewPollKey = null
  let previewPollAttempts = 0
  let listPath = null

  // ── Computed ──────────────────────────────────────────────────────────────

  const hasSelection  = computed(() => selectedIds.value.length > 0)
  const selectedCount = computed(() => selectedIds.value.length)

  // ── Helpers ───────────────────────────────────────────────────────────────

  function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  function defaultSortDir(column) {
    return column === 'name' ? 'asc' : 'desc'
  }

  function stopPreviewPolling() {
    if (previewPollTimer) {
      clearTimeout(previewPollTimer)
      previewPollTimer = null
    }
    previewPollKey = null
    previewPollAttempts = 0
  }

  async function fetchPreviewStatuses(ids) {
    if (!ids.length) return []
    const params = new URLSearchParams()
    ids.forEach(id => params.append('ids[]', String(id)))
    const res = await fetch(`/drive/preview_statuses.json?${params}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken()
      }
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    return data.files ?? []
  }

  function schedulePreviewPolling() {
    stopPreviewPolling()
    const pending = files.value.filter(fileNeedsPreviewPoll)
    if (!pending.length) return

    const key = listPath || location.pathname
    previewPollKey = key
    previewPollAttempts = 0

    const tick = async () => {
      if (previewPollKey !== key) return
      previewPollAttempts += 1
      if (previewPollAttempts > PREVIEW_POLL_MAX_ATTEMPTS) {
        stopPreviewPolling()
        return
      }

      const ids = files.value.filter(fileNeedsPreviewPoll).map(f => f.id)
      if (!ids.length) {
        stopPreviewPolling()
        return
      }

      try {
        const updates = await fetchPreviewStatuses(ids)
        if (previewPollKey !== key) return
        if (updates.length) {
          const byId = new Map(updates.map(u => [u.id, u]))
          files.value = files.value.map(f => {
            const u = byId.get(f.id)
            return u ? { ...f, preview_url: u.preview_url, preview_state: u.preview_state } : f
          })
        }
      } catch (_e) {
        // Keep polling; transient errors should not stop preview resolution.
      }

      if (previewPollKey !== key) return
      if (!files.value.some(fileNeedsPreviewPoll)) {
        stopPreviewPolling()
        return
      }
      previewPollTimer = setTimeout(tick, PREVIEW_POLL_INTERVAL_MS)
    }

    previewPollTimer = setTimeout(tick, PREVIEW_POLL_INTERVAL_MS)
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  async function loadFolder(path, { silent = false, clearSelection: shouldClearSelection = true } = {}) {
    if (!silent) {
      stopPreviewPolling()
      loading.value = true
    }
    error.value = null
    listPath = path
    try {
      const url = withListParams(path, {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        sortColumn: sortColumn.value,
        sortDir: sortDir.value
      })
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken()
        }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const pagination = data.pagination || {}
      batch(() => {
        folders.value       = data.folders       ?? []
        files.value         = data.files         ?? []
        breadcrumbs.value   = data.breadcrumbs   ?? []
        currentFolder.value = data.current_folder ?? null
        hasMore.value       = Boolean(pagination.has_more)
        nextOffset.value    = pagination.next_offset ?? (folders.value.length + files.value.length)
        if (shouldClearSelection) selectedIds.value = []
      })
      if (!silent) schedulePreviewPolling()
    } catch (e) {
      error.value = e.message
    } finally {
      if (!silent) loading.value = false
    }
  }

  async function loadMore() {
    if (!hasMore.value || loadingMore.value || loading.value || !listPath) return

    loadingMore.value = true
    error.value = null
    try {
      const url = withListParams(listPath, {
        offset: nextOffset.value,
        limit: DEFAULT_PAGE_LIMIT,
        sortColumn: sortColumn.value,
        sortDir: sortDir.value
      })
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken()
        }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const pagination = data.pagination || {}
      batch(() => {
        const newFolders = data.folders ?? []
        const newFiles = data.files ?? []
        if (newFolders.length) {
          const seen = new Set(folders.value.map(f => f.id))
          folders.value = [...folders.value, ...newFolders.filter(f => !seen.has(f.id))]
        }
        if (newFiles.length) {
          const seen = new Set(files.value.map(f => f.id))
          files.value = [...files.value, ...newFiles.filter(f => !seen.has(f.id))]
        }
        hasMore.value = Boolean(pagination.has_more)
        nextOffset.value = pagination.next_offset ?? nextOffset.value + newFolders.length + newFiles.length
      })
      schedulePreviewPolling()
    } catch (e) {
      error.value = e.message
    } finally {
      loadingMore.value = false
    }
  }

  function selectItem(id, multi = false) {
    if (multi) {
      const current = selectedIds.value
      selectedIds.value = current.includes(id)
        ? current.filter(i => i !== id)
        : [...current, id]
    } else {
      selectedIds.value =
        selectedIds.value[0] === id && selectedIds.value.length === 1 ? [] : [id]
    }
  }

  function clearSelection() {
    selectedIds.value = []
  }

  function selectOnly(id) {
    selectedIds.value = [id]
  }

  function setSort(column) {
    if (!['name', 'size', 'created'].includes(column)) return
    if (sortColumn.value === column) {
      sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortColumn.value = column
      sortDir.value = defaultSortDir(column)
    }
    if (listPath) loadFolder(listPath, { clearSelection: false })
  }

  function addFiles(newFiles) {
    files.value = [...files.value, ...newFiles]
  }

  function removeFile(id) {
    files.value       = files.value.filter(f => f.id !== id)
    selectedIds.value = selectedIds.value.filter(i => i !== `file-${id}`)
  }

  function addFolder(folder) {
    folders.value = [...folders.value, folder]
  }

  function removeFolder(id) {
    folders.value     = folders.value.filter(f => f.id !== id)
    selectedIds.value = selectedIds.value.filter(i => i !== `folder-${id}`)
  }

  function updateFile(id, attrs) {
    files.value = files.value.map(f => f.id === id ? { ...f, ...attrs } : f)
  }

  function updateFolder(id, attrs) {
    folders.value = folders.value.map(f => f.id === id ? { ...f, ...attrs } : f)
  }

  return {
    // signals
    folders, files, breadcrumbs, currentFolder,
    selectedIds, loading, loadingMore, error, searchQuery,
    sortColumn, sortDir, hasMore, nextOffset,
    // computed
    hasSelection, selectedCount,
    // actions
    loadFolder, loadMore, selectItem, clearSelection, selectOnly, setSort,
    addFiles, removeFile, addFolder, removeFolder,
    updateFile, updateFolder,
    stopPreviewPolling
  }
}
